import { Suspense, useEffect } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { AtsJobApplicationPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { JobApplicationCvSection } from './CvSection'

const JobApplicationPage = observer(() => {
  const { company, access } = useCurrentSession()
  const params = useParams()
  const applicationId = Number(params.application_id)
  const jobId = Number(params.jobId)
  const { navigation } = useNavigation()
  const location = useLocation()
  const navigate = useNavigate()

  const { data, loading } = useQuery(AtsJobApplicationPageDocument, {
    variables: { accessId: access.id, applicationId, companyId: company.id },
  })

  const application = data?.ats.application
  const canManageJobPostings = data?.ats._policies.manageJobPostings.can ?? false
  const hiringRole = application?.jobPosting?.currentHiringRole[0]?.hiringRole ?? 'editor'
  const isReviewerRole = hiringRole === 'reviewer' && !canManageJobPostings

  const allLoaded = !loading && application && data

  // Redirect reviewer role to notes page when on index route
  useEffect(() => {
    if (isReviewerRole) {
      const isOnIndexRoute = location.pathname.includes(
        navigation.ats.jobs.jobsId.applications.applicationDetail.index.fullPath({
          jobId,
          application_id: applicationId,
        })
      )
      const isOnNotesRoute = location.pathname.includes(
        navigation.ats.jobs.jobsId.applications.applicationDetail.notes.fullPath({
          jobId,
          application_id: applicationId,
        })
      )

      if (isOnIndexRoute && !isOnNotesRoute) {
        track(trackingEvents.ats.reviewerCandidateViewed, {
          ats_application_id: applicationId,
          ats_job_id: jobId,
        })
        navigate(
          navigation.ats.jobs.jobsId.applications.applicationDetail.notes.fullPath({
            jobId,
            application_id: applicationId,
          }),
          { replace: true }
        )
      }
    }
  }, [isReviewerRole, jobId, applicationId])

  if (!allLoaded) {
    return (
      <Box paddingY="s12" alignItems="center" justifyContent="center" height="full">
        <Spinner size="s40" />
      </Box>
    )
  }

  return (
    <Box height="full">
      <Box
        flexGrow
        flexDirection="row"
        overflowY="hidden"
        border={
          isReviewerRole ? { top: { style: 'solid', width: 's1', color: 'grey200' } } : undefined
        }
      >
        <Box
          flexNoShrink
          height="full"
          overflowX="hidden"
          border={{ right: { style: 'solid', width: 's1', color: 'grey200' } }}
          style={{ width: '50%' }}
        >
          {/* TO DO: if each tab page has a skeleton, we can remove the fallback */}
          <Suspense fallback={<Spinner size="s40" />}>
            <Outlet context={{ application, isReviewerRole, hiringRole }} />
          </Suspense>
        </Box>
        <Box flexGrow height="full" overflowY="auto">
          <JobApplicationCvSection data={application} />
        </Box>
      </Box>
    </Box>
  )
})

export default JobApplicationPage
